import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell, PieChart, Pie, ComposedChart
} from 'recharts';
import './DashboardPlanejador.css';

const DashboardPlanejador = () => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  // Check permissions
  const hasPermission = userData?.permissions?.includes('Master') || userData?.permissions?.includes('Diretoria');

  // Fetch diagnostics
  const fetchDiagnostics = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/dashboard/diagnostics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar diagnósticos');
      }

      const data = await response.json();
      setDiagnostics(data.data);
      setShowDiagnostics(true);
    } catch (err) {
      console.error('Erro ao buscar diagnósticos:', err);
      alert('Erro ao buscar diagnósticos: ' + err.message);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';
      
      const response = await fetch(
        `${API_URL}/api/dashboard/work-planner?month=${selectedMonth}&year=${selectedYear}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar dados do dashboard');
      }

      const data = await response.json();
      setDashboardData(data.data);
    } catch (err) {
      console.error('Erro ao buscar dados do dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      fetchDashboardData();
    }
  }, [selectedMonth, selectedYear, hasPermission]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(2)}%`;
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{label}</p>
          {payload.map((entry, index) => {
            // Only format as currency if it's actually a monetary value
            const isCurrency = entry.dataKey === 'totalConsolidado' || 
                             entry.dataKey === 'totalRevenue' || 
                             entry.dataKey === 'totalPrevisto' ||
                             entry.dataKey === 'totalValue' ||
                             entry.dataKey === 'valor' ||
                             (entry.dataKey.includes('Value') || entry.dataKey.includes('Revenue'));
            
            // Format percentages differently
            const isPercentage = entry.dataKey === 'completionRate' || entry.dataKey.includes('percentage');
            
            return (
              <p key={index} style={{ color: entry.color }}>
                {entry.name}: {isCurrency ? formatCurrency(entry.value) : (isPercentage ? `${entry.value}%` : entry.value)}
              </p>
            );
          })}
          {/* Show totalConsolidado for work distribution chart */}
          {payload[0]?.payload?.totalConsolidado !== undefined && (
            <p style={{ color: '#666', fontWeight: 'bold', borderTop: '1px solid #ddd', paddingTop: '5px', marginTop: '5px' }}>
              Total Consolidado: {formatCurrency(payload[0].payload.totalConsolidado)}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Colors for charts
  const COLORS = ['#4ade80', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#10b981', '#6366f1'];

  if (!hasPermission) {
    return (
      <div className="dashboard-error">
        <h3>Acesso Negado</h3>
        <p>Você precisa ter permissão de Master ou Diretoria para acessar este dashboard.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="dashboard-loading">Carregando dados do dashboard...</div>;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h3>Erro ao carregar dashboard</h3>
        <p>{error}</p>
        <button onClick={fetchDashboardData}>Tentar novamente</button>
      </div>
    );
  }

  if (!dashboardData) {
    return <div className="dashboard-error">Nenhum dado disponível</div>;
  }

  const { overviewCards, charts, overdueItems, monthlyComparison } = dashboardData;

  return (
    <div className="dashboard-planejador">
      <div className="dashboard-header">
        <h2>Dashboard Planejador de Trabalho</h2>
        <div className="date-selector">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i}>
                {new Date(2000, i, 1).toLocaleString('pt-BR', { month: 'long' })}
              </option>
            ))}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - 2 + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
          <button onClick={fetchDashboardData} className="refresh-button">
            Atualizar
          </button>
          {userData?.permissions?.includes('Master') && (
            <button onClick={fetchDiagnostics} className="diagnostic-button">
              Diagnósticos
            </button>
          )}
        </div>
      </div>

      {/* Diagnostics Panel */}
      {showDiagnostics && diagnostics && (
        <div className="diagnostics-panel">
          <div className="diagnostics-header">
            <h3>Diagnósticos do Sistema</h3>
            <button onClick={() => setShowDiagnostics(false)}>Fechar</button>
          </div>
          <div className="diagnostics-content">
            <p><strong>Total de Itens de Trabalho:</strong> {diagnostics.totalWorkItems}</p>
            <p><strong>Total de Usuários:</strong> {diagnostics.totalUsers}</p>
            <p><strong>Total de Clientes:</strong> {diagnostics.totalClientes}</p>
            <p><strong>Itens com Responsável:</strong> {diagnostics.workItemsWithAssignedTo}</p>
            <p><strong>Itens com Serviços:</strong> {diagnostics.workItemsWithServicos}</p>
            <p><strong>Itens com Atividades:</strong> {diagnostics.workItemsWithActivities}</p>
            <p><strong>Itens Ativos (A Fazer + Em Progresso):</strong> {diagnostics.activeWorkItems}</p>
            {diagnostics.workItemsGroupedByUser && diagnostics.workItemsGroupedByUser[0] && (
              <>
                <p style={{ marginTop: '15px', fontWeight: 'bold', borderTop: '1px solid #ddd', paddingTop: '10px' }}>Distribuição de Itens por Usuário:</p>
                <p><strong>Por Responsável:</strong></p>
                {diagnostics.workItemsGroupedByUser[0].byAssigned.map((item, idx) => (
                  <p key={`assigned-${idx}`} style={{ marginLeft: '20px', fontSize: '12px' }}>
                    {item.userName || 'Usuário não encontrado'}: {item.count} itens
                  </p>
                ))}
                <p><strong>Por Criador:</strong></p>
                {diagnostics.workItemsGroupedByUser[0].byCreated.map((item, idx) => (
                  <p key={`created-${idx}`} style={{ marginLeft: '20px', fontSize: '12px' }}>
                    {item.userName || 'Usuário não encontrado'}: {item.count} itens
                  </p>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="overview-cards">
        <div className="overview-card">
          <h3>Status dos Trabalhos</h3>
          <div className="card-content">
            <div className="status-summary">
              <div className="status-item">
                <span className="status-label">A Fazer:</span>
                <span className="status-value">{overviewCards.statusSummary.todo}</span>
              </div>
              <div className="status-item">
                <span className="status-label">Em Progresso:</span>
                <span className="status-value">{overviewCards.statusSummary.inProgress}</span>
              </div>
              <div className="status-item">
                <span className="status-label">Concluído:</span>
                <span className="status-value">{overviewCards.statusSummary.done}</span>
              </div>
              <div className="status-item total">
                <span className="status-label">Total:</span>
                <span className="status-value">{overviewCards.statusSummary.total}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <h3>Resumo Financeiro</h3>
          <div className="card-content">
            <div className="financial-summary">
              <div className="financial-item">
                <span className="financial-label">Previsto:</span>
                <span className="financial-value">{formatCurrency(overviewCards.financialSummary.totalPrevisto)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Consolidado:</span>
                <span className="financial-value">{formatCurrency(overviewCards.financialSummary.totalConsolidado)}</span>
              </div>
              <div className="financial-item percentage">
                <span className="financial-label">Realização:</span>
                <span className="financial-value">{formatPercentage(overviewCards.financialSummary.percentage)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <h3>Produtividade Mensal</h3>
          <div className="card-content">
            <div className="productivity-summary">
              <div className="productivity-item">
                <span className="productivity-label">Este mês:</span>
                <span className="productivity-value">{overviewCards.monthlyProductivity.current}</span>
              </div>
              <div className="productivity-item">
                <span className="productivity-label">Mês anterior:</span>
                <span className="productivity-value">{overviewCards.monthlyProductivity.last}</span>
              </div>
              <div className={`productivity-item change ${parseFloat(overviewCards.monthlyProductivity.change) >= 0 ? 'positive' : 'negative'}`}>
                <span className="productivity-label">Variação:</span>
                <span className="productivity-value">
                  {parseFloat(overviewCards.monthlyProductivity.change) >= 0 ? '+' : ''}
                  {formatPercentage(overviewCards.monthlyProductivity.change)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="overview-card">
          <h3>Equipe Ativa</h3>
          <div className="card-content">
            <div className="team-summary">
              <div className="team-value">{overviewCards.activeTeamMembers}</div>
              <div className="team-label">Membros com tarefas ativas</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Monthly Financial Performance */}
        <div className="chart-container full-width">
          <h3>Desempenho Financeiro Mensal (Últimos 12 meses)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={charts.monthlyFinancial}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="totalPrevisto" stroke="#3b82f6" name="Total Previsto" />
              <Line type="monotone" dataKey="totalConsolidado" stroke="#4ade80" name="Total Consolidado" />
              <Line type="monotone" dataKey="completionRate" stroke="#f59e0b" name="Taxa de Conclusão (%)" yAxisId="right" />
              <YAxis yAxisId="right" orientation="right" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Work Distribution by User */}
        <div className="chart-container half-width">
          <h3>Distribuição de Trabalho por Usuário</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={charts.workDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="userName" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="totalItems" fill="#3b82f6" name="Total de Itens" />
              <Bar dataKey="completedItems" fill="#4ade80" name="Itens Concluídos" />
            </BarChart>
          </ResponsiveContainer>
          {charts.workDistribution.length > 0 && charts.workDistribution[0].userName !== 'Sem dados' && (
            <div className="chart-subtitle">
              <small>Total Consolidado por usuário mostrado na tooltip</small>
            </div>
          )}
        </div>

        {/* Top 10 Clients */}
        <div className="chart-container half-width">
          <h3>Top 10 Clientes (Por Total Consolidado)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={charts.topClients}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="clienteName" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                interval={0}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalConsolidado" fill="#4ade80" name="Total Consolidado" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Service Type Performance */}
        <div className="chart-container half-width">
          <h3>Desempenho por Tipo de Serviço (TipoCliente)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={charts.serviceTypePerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="_id" 
                angle={-45} 
                textAnchor="end" 
                height={100}
                interval={0}
              />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalRevenue" fill="#8b5cf6" name="Receita Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Work Flow */}
        <div className="chart-container half-width">
          <h3>Fluxo de Trabalho Mensal (Itens Atualizados no Mês)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={charts.monthlyWorkFlow}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="todo" fill="#3b82f6" name="A Fazer" />
              <Bar dataKey="inProgress" fill="#f59e0b" name="Em Progresso" />
              <Bar dataKey="done" fill="#4ade80" name="Concluídos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Overdue Items Alert */}
      {overdueItems && overdueItems.length > 0 && (
        <div className="overdue-section">
          <h3>Itens em Atraso</h3>
          <div className="overdue-table">
            <table>
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Cliente</th>
                  <th>Responsável</th>
                  <th>Vencimento</th>
                  <th>Dias em Atraso</th>
                  <th>Prioridade</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {overdueItems.map(item => (
                  <tr key={item._id}>
                    <td>{item.title}</td>
                    <td>{item.clienteName}</td>
                    <td>{item.assignedTo}</td>
                    <td>{new Date(item.dueDate).toLocaleDateString('pt-BR')}</td>
                    <td className="days-overdue">{item.daysOverdue}</td>
                    <td className={`priority ${item.priority}`}>{item.priority}</td>
                    <td>{formatCurrency(item.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Comparison Table */}
      {monthlyComparison && (
        <div className="comparison-section">
          <h3>Comparação Mensal</h3>
          <div className="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Métrica</th>
                  <th>{monthlyComparison.current.month}</th>
                  <th>{monthlyComparison.last.month}</th>
                  <th>Variação</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total de Itens</td>
                  <td>{monthlyComparison.current.totalItems}</td>
                  <td>{monthlyComparison.last.totalItems}</td>
                  <td className={parseFloat(monthlyComparison.changes.totalItems) >= 0 ? 'positive' : 'negative'}>
                    {parseFloat(monthlyComparison.changes.totalItems) >= 0 ? '+' : ''}
                    {formatPercentage(monthlyComparison.changes.totalItems)}
                  </td>
                </tr>
                <tr>
                  <td>Total Previsto</td>
                  <td>{formatCurrency(monthlyComparison.current.totalPrevisto)}</td>
                  <td>{formatCurrency(monthlyComparison.last.totalPrevisto)}</td>
                  <td className={parseFloat(monthlyComparison.changes.totalPrevisto) >= 0 ? 'positive' : 'negative'}>
                    {parseFloat(monthlyComparison.changes.totalPrevisto) >= 0 ? '+' : ''}
                    {formatPercentage(monthlyComparison.changes.totalPrevisto)}
                  </td>
                </tr>
                <tr>
                  <td>Total Consolidado</td>
                  <td>{formatCurrency(monthlyComparison.current.totalConsolidado)}</td>
                  <td>{formatCurrency(monthlyComparison.last.totalConsolidado)}</td>
                  <td className={parseFloat(monthlyComparison.changes.totalConsolidado) >= 0 ? 'positive' : 'negative'}>
                    {parseFloat(monthlyComparison.changes.totalConsolidado) >= 0 ? '+' : ''}
                    {formatPercentage(monthlyComparison.changes.totalConsolidado)}
                  </td>
                </tr>
                <tr>
                  <td>Itens Concluídos</td>
                  <td>{monthlyComparison.current.completed}</td>
                  <td>{monthlyComparison.last.completed}</td>
                  <td className={parseFloat(monthlyComparison.changes.completed) >= 0 ? 'positive' : 'negative'}>
                    {parseFloat(monthlyComparison.changes.completed) >= 0 ? '+' : ''}
                    {formatPercentage(monthlyComparison.changes.completed)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPlanejador;
